import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { limparDocumento, erroDocumento } from '../utils/documento';
import { navegarComEnter } from '../utils/enterNavegacao';
import { useAuth } from '../contexts/AuthContext';
import { configParticipante } from '../config/participantes';
import { hojeBR, somarMeses } from '../utils/data';
import InputDataFiltro from '../components/common/InputDataFiltro';
import AbaRecorrencia from '../components/clientes/AbaRecorrencia';
import useRecorrenciaHabilitada from '../hooks/useRecorrenciaHabilitada';
import s from './CadastroClientes.module.css';

/* ────────────────────────────────────────────────────────
   Constantes
   ──────────────────────────────────────────────────────── */
// Rótulo da aba "Participantes" depende do segmento da organização (ver
// config/participantes.js) — montada dentro do componente porque precisa
// do usuário logado.
const ABAS_BASE = [
  { id: 'geral',         label: 'Geral' },
  { id: 'financeiro',    label: 'Financeiro' },
  { id: 'movimento',     label: 'Movimento/Pedidos' },
  { id: 'participantes', label: 'Participantes' },
  { id: 'obs',           label: 'Obs.' },
  { id: 'anexos',        label: 'Anexos' },
];

const TIPOS_CONTRIBUINTE = [
  { v: '1', l: '1 — Contribuinte ICMS' },
  { v: '2', l: '2 — Contribuinte isento' },
  { v: '9', l: '9 — Não contribuinte' },
];

const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
  'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

/* ────────────────────────────────────────────────────────
   Estado vazio
   ──────────────────────────────────────────────────────── */
const FORM_VAZIO = {
  id: null,
  codigo: '',
  nome: '',
  nome_fantasia: '',
  tipo_pessoa: 'F',
  inativo: false,
  // Identificação
  cpf_cnpj: '',
  rg: '',
  ie: '',
  tipo_contribuinte: '9',
  profissao: '',
  data_nascimento: '',
  // Endereço
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  ponto_referencia: '',
  // Contato
  email: '',
  site: '',
  fone1: '',
  fone2: '',
  fone3: '',
  // Financeiro
  limite_credito: '',
  credito_utilizado: '',
  desconto_perc: '',
  tabela_preco_id: null,
  tabela_preco_nome: '',
  plano_fidelidade: '',
  cartao_fidelidade: '',
  nao_gera_comissao: false,
  nfse_iss_retido: false,
  // Obs
  obs: '',
};

/* ────────────────────────────────────────────────────────
   Mapeamento API → form
   ──────────────────────────────────────────────────────── */
const sv = v => (v == null ? '' : String(v));

const clienteParaForm = c => ({
  id:               c.id,
  codigo:           sv(c.codigo),
  nome:             c.nome ?? '',
  nome_fantasia:    c.nome_fantasia ?? '',
  tipo_pessoa:      c.tipo_pessoa ?? 'F',
  inativo:          !(c.ativo ?? true),
  cpf_cnpj:         c.cpf_cnpj ?? '',
  rg:               c.rg ?? '',
  ie:               c.ie ?? '',
  tipo_contribuinte: c.tipo_contribuinte ?? '9',
  profissao:        c.profissao ?? '',
  data_nascimento:  c.data_nascimento ? c.data_nascimento.slice(0, 10) : '',
  cep:              c.cep ?? '',
  logradouro:       c.logradouro ?? '',
  numero:           c.numero ?? '',
  complemento:      c.complemento ?? '',
  bairro:           c.bairro ?? '',
  cidade:           c.cidade ?? '',
  estado:           c.estado?.trim() ?? '',
  ponto_referencia: c.ponto_referencia ?? '',
  email:            c.email ?? '',
  site:             c.site ?? '',
  fone1:            c.fone1 ?? '',
  fone2:            c.fone2 ?? '',
  fone3:            c.fone3 ?? '',
  limite_credito:   sv(c.limite_credito ?? ''),
  credito_utilizado: sv(c.credito_utilizado ?? ''),
  desconto_perc:    sv(c.desconto_perc ?? ''),
  tabela_preco_id:  c.tabela_preco_id ?? null,
  tabela_preco_nome: c.tabela_preco_nome ?? '',
  plano_fidelidade: c.plano_fidelidade ?? '',
  cartao_fidelidade: c.cartao_fidelidade ?? '',
  nao_gera_comissao: !!c.nao_gera_comissao,
  nfse_iss_retido: !!c.nfse_iss_retido,
  obs:              c.obs ?? '',
});

/* ────────────────────────────────────────────────────────
   Modal — Buscar cliente
   ──────────────────────────────────────────────────────── */
function ModalBuscaCliente({ onSelect, onClose }) {
  const [busca, setBusca] = useState('');
  const [sel, setSel]     = useState(null);

  const { data: lista = [], isFetching } = useQuery({
    queryKey: ['clientes-busca-modal', busca],
    queryFn: () => api.get('/clientes', { params: { busca, todos: 'true' } }).then(r => r.data),
    keepPreviousData: true,
  });

  return (
    <div className={s.overlay} data-enter-scope="off">
      <div className={s.modal} style={{ width: 580 }}>
        <div className={s.modalTitle}>Buscar Cliente</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input
              className={s.inputErp} style={{ flex: 1 }}
              placeholder="Nome, CPF/CNPJ, e-mail ou telefone..."
              value={busca} onChange={e => setBusca(e.target.value)}
              autoFocus
            />
            {isFetching && <span style={{ fontSize: 12, color: '#888' }}>buscando...</span>}
          </div>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>Código</th>
                <th>Nome</th>
                <th style={{ width: 130 }}>CPF/CNPJ</th>
                <th style={{ width: 120 }}>Fone</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(c => (
                <tr key={c.id}
                  className={sel?.id === c.id ? s.selecionado : ''}
                  onClick={() => setSel(c)}
                  onDoubleClick={() => onSelect(c)}
                >
                  <td>{c.codigo}</td>
                  <td>{c.nome}</td>
                  <td>{c.cpf_cnpj || '—'}</td>
                  <td>{c.fone1 || '—'}</td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td colSpan={4} style={{ padding: 10, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                  Nenhum cliente encontrado
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => sel && onSelect(sel)} disabled={!sel}>Selecionar</button>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Buscar tabela de preço
   ──────────────────────────────────────────────────────── */
function ModalBuscaTabela({ onSelect, onClose }) {
  const [busca, setBusca] = useState('');
  const [sel, setSel]     = useState(null);

  const { data: lista = [], isFetching } = useQuery({
    queryKey: ['tabelas-preco-modal', busca],
    queryFn: () => api.get('/tabelas-preco', { params: { busca } }).then(r => r.data),
    keepPreviousData: true,
  });

  return (
    <div className={s.overlay} data-enter-scope="off">
      <div className={s.modal} style={{ width: 480 }}>
        <div className={s.modalTitle}>Selecionar Tabela de Preço</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input className={s.inputErp} style={{ flex: 1 }}
              placeholder="Código ou descrição..."
              value={busca} onChange={e => setBusca(e.target.value)} autoFocus />
            {isFetching && <span style={{ fontSize: 12, color: '#888' }}>buscando...</span>}
          </div>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>Cód.</th>
                <th>Descrição</th>
                <th style={{ width: 80 }}>Produtos</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(t => (
                <tr key={t.id}
                  className={sel?.id === t.id ? s.selecionado : ''}
                  onClick={() => setSel(t)}
                  onDoubleClick={() => onSelect(t)}
                >
                  <td>{t.codigo}</td>
                  <td>{t.descricao}</td>
                  <td style={{ textAlign: 'center' }}>{t.total_produtos}</td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td colSpan={3} style={{ padding: 10, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                  Nenhuma tabela encontrada
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => sel && onSelect(sel)} disabled={!sel}>Selecionar</button>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Geral
   ──────────────────────────────────────────────────────── */
function AbaGeral({ form, setForm, editando }) {
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));
  const dis = !editando;
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);

  const tipoPessoa = form.tipo_pessoa === 'J' ? 'J' : 'F';

  const buscarCep = async () => {
    const cep = form.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setForm(f => ({
          ...f,
          logradouro:  d.logradouro  || f.logradouro,
          bairro:      d.bairro      || f.bairro,
          cidade:      d.localidade  || f.cidade,
          estado:      d.uf          || f.estado,
          complemento: d.complemento || f.complemento,
        }));
      }
    } catch { /* ignora erros de rede */ }
  };

  const buscarCnpj = async () => {
    const cnpj = limparDocumento(form.cpf_cnpj); // preserva letras (CNPJ alfanumérico)
    if (cnpj.length !== 14) { alert('Informe um CNPJ com 14 caracteres.'); return; }
    setBuscandoCnpj(true);
    try {
      const r = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (!r.ok) throw new Error('CNPJ não encontrado na Receita Federal.');
      const d = await r.json();

      const ie  = d.inscricoes_estaduais?.find(i => i.ativo)?.inscricao_estadual ?? '';
      const logr = [d.descricao_tipo_de_logradouro, d.logradouro].filter(Boolean).join(' ');
      const cepFmt = d.cep
        ? d.cep.replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2')
        : '';
      const formatFone = raw => {
        if (!raw) return '';
        const digits = raw.replace(/\D/g, '');
        if (digits.length === 11) return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
        if (digits.length === 10) return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
        return raw.trim();
      };

      setForm(f => ({
        ...f,
        nome:              d.razao_social            || f.nome,
        nome_fantasia:     d.nome_fantasia            || f.nome_fantasia,
        ie,
        tipo_contribuinte: ie ? '1' : '9',
        logradouro:        logr                      || f.logradouro,
        numero:            d.numero                  || f.numero,
        complemento:       d.complemento             || f.complemento,
        bairro:            d.bairro                  || f.bairro,
        cidade:            d.municipio               || f.cidade,
        estado:            d.uf                      || f.estado,
        cep:               cepFmt                    || f.cep,
        email:             d.email                   || f.email,
        fone1:             formatFone(d.ddd_telefone_1) || f.fone1,
      }));
    } catch (err) {
      alert(err.message || 'Erro ao consultar o CNPJ. Tente novamente.');
    } finally {
      setBuscandoCnpj(false);
    }
  };

  return (
    <div className={s.geralGrid}>
      {/* ── Coluna esquerda ── */}
      <div className={s.geralEsq}>
        <div className={s.fieldset}>
          <div className={s.legend}>Identificação</div>

          <div className={s.campo}>
            <span className={s.campoLabel}>{tipoPessoa === 'J' ? 'CNPJ' : 'CPF'}</span>
            <input className={s.inputErp} style={{ width: 160 }}
              value={form.cpf_cnpj} readOnly={dis}
              onChange={e => set('cpf_cnpj', e.target.value)}
              placeholder={tipoPessoa === 'J' ? '00.000.000/0000-00' : '000.000.000-00'} />
            {tipoPessoa === 'J' && !dis && (
              <button className={s.btnLookup} onClick={buscarCnpj}
                disabled={buscandoCnpj} title="Consultar CNPJ na Receita Federal">
                {buscandoCnpj ? '⏳' : '🔍'}
              </button>
            )}
          </div>

          {tipoPessoa === 'F' ? (
            <div className={s.campo}>
              <span className={s.campoLabel}>RG</span>
              <input className={s.inputErp} style={{ width: 130 }}
                value={form.rg} readOnly={dis}
                onChange={e => set('rg', e.target.value)} />
            </div>
          ) : (
            <>
              <div className={s.campo}>
                <span className={s.campoLabel}>Nome Fantasia</span>
                <input className={s.inputErp} style={{ width: 220 }}
                  value={form.nome_fantasia} readOnly={dis}
                  onChange={e => set('nome_fantasia', e.target.value)}
                  placeholder="Nome popular/comercial..." />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Insc. Estadual</span>
                <input className={s.inputErp} style={{ width: 160 }}
                  value={form.ie} readOnly={dis}
                  onChange={e => set('ie', e.target.value)} />
              </div>
            </>
          )}

          <div className={s.campo}>
            <span className={s.campoLabel}>Tipo contribuinte</span>
            <select className={s.selectErp} style={{ flex: 1 }}
              value={form.tipo_contribuinte} disabled={dis}
              onChange={e => set('tipo_contribuinte', e.target.value)}>
              {TIPOS_CONTRIBUINTE.map(t => (
                <option key={t.v} value={t.v}>{t.l}</option>
              ))}
            </select>
          </div>

          {tipoPessoa === 'F' && (
            <>
              <div className={s.campo}>
                <span className={s.campoLabel}>Profissão</span>
                <input className={s.inputErp} style={{ flex: 1 }}
                  value={form.profissao} readOnly={dis}
                  onChange={e => set('profissao', e.target.value)} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Data nascimento</span>
                <input className={s.inputErp} type="date" style={{ width: 140 }}
                  value={form.data_nascimento} readOnly={dis}
                  onChange={e => set('data_nascimento', e.target.value)} />
              </div>
            </>
          )}
        </div>

        <div className={s.fieldset}>
          <div className={s.legend}>Endereço</div>

          <div className={s.campo}>
            <span className={s.campoLabel}>CEP</span>
            <input className={s.inputErp} style={{ width: 100 }}
              value={form.cep} readOnly={dis}
              onChange={e => set('cep', e.target.value)}
              onBlur={buscarCep}
              placeholder="00000-000" />
            {!dis && (
              <button className={s.btnLookup} onClick={buscarCep} title="Buscar CEP">
                🔍
              </button>
            )}
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Logradouro</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.logradouro} readOnly={dis}
              onChange={e => set('logradouro', e.target.value)} />
          </div>

          <div className={s.endRow}>
            <span className={s.campoLabel} style={{ minWidth: 110, textAlign: 'right' }}>Número</span>
            <input className={s.inputErp} style={{ width: 80 }}
              value={form.numero} readOnly={dis}
              onChange={e => set('numero', e.target.value)} />
            <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Compl.</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.complemento} readOnly={dis}
              onChange={e => set('complemento', e.target.value)} />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Bairro</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.bairro} readOnly={dis}
              onChange={e => set('bairro', e.target.value)} />
          </div>

          <div className={s.endRow}>
            <span className={s.campoLabel} style={{ minWidth: 110, textAlign: 'right' }}>Cidade</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.cidade} readOnly={dis}
              onChange={e => set('cidade', e.target.value)} />
            <span style={{ fontSize: 13 }}>UF</span>
            <select className={s.selectErp} style={{ width: 60 }}
              value={form.estado} disabled={dis}
              onChange={e => set('estado', e.target.value)}>
              <option value=""></option>
              {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>P. referência</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.ponto_referencia} readOnly={dis}
              onChange={e => set('ponto_referencia', e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Coluna direita ── */}
      <div className={s.geralDir}>
        <div className={s.fieldset}>
          <div className={s.legend}>Contato</div>

          <div className={s.campo}>
            <span className={s.campoLabel}>E-mail</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              type="email"
              value={form.email} readOnly={dis}
              onChange={e => set('email', e.target.value)} />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Site</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.site} readOnly={dis}
              onChange={e => set('site', e.target.value)}
              placeholder="www.exemplo.com.br" />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Fone 1</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.fone1} readOnly={dis}
              onChange={e => set('fone1', e.target.value)}
              placeholder="(00) 00000-0000" />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Fone 2</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.fone2} readOnly={dis}
              onChange={e => set('fone2', e.target.value)} />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Fone 3</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.fone3} readOnly={dis}
              onChange={e => set('fone3', e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Financeiro
   ──────────────────────────────────────────────────────── */
function AbaFinanceiro({ form, setForm, editando, onAbrirTabela }) {
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));
  const dis = !editando;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
      <div className={s.fieldset}>
        <div className={s.legend}>Crédito</div>

        <div className={s.campo} title="Em branco = sem limite definido (bloqueia vendas a prazo se a rotina 'Validar limite de crédito' estiver ativa em Parâmetros → Rotinas)">
          <span className={s.campoLabel}>Limite R$</span>
          <input className={s.inputErp} style={{ width: 120, textAlign: 'right' }}
            type="number" step="0.01" min="0"
            value={form.limite_credito} readOnly={dis}
            onChange={e => set('limite_credito', e.target.value)} />
        </div>

        <div className={s.campo}>
          <span className={s.campoLabel}>Utilizado R$</span>
          <input className={s.inputErp} style={{ width: 120, textAlign: 'right' }}
            value={form.credito_utilizado || '0.00'} readOnly
            title="Calculado automaticamente pelas vendas a prazo" />
        </div>
      </div>

      <div className={s.fieldset}>
        <div className={s.legend}>Comercial</div>

        <div className={s.campo}>
          <span className={s.campoLabel}>Desconto %</span>
          <input className={s.inputErp} style={{ width: 80, textAlign: 'right' }}
            type="number" step="0.01" min="0" max="100"
            value={form.desconto_perc} readOnly={dis}
            onChange={e => set('desconto_perc', e.target.value)} />
        </div>

        <div className={s.campo}>
          <span className={s.campoLabel}>Tab. de Preço</span>
          <input className={s.inputErp} style={{ flex: 1 }}
            value={form.tabela_preco_nome} readOnly
            placeholder="Nenhuma" />
          <button className={s.btnLookup} disabled={dis}
            onClick={onAbrirTabela} title="Selecionar tabela de preço">
            ...
          </button>
          {!dis && form.tabela_preco_id && (
            <button className={s.btnLookup}
              onClick={() => setForm(f => ({ ...f, tabela_preco_id: null, tabela_preco_nome: '' }))}
              title="Remover tabela">
              ✕
            </button>
          )}
        </div>

        <div className={s.checkRow} title="Marcado, as vendas feitas para este cliente não entram no relatório de Comissões">
          <input type="checkbox" id="chkNaoGeraComissao"
            checked={form.nao_gera_comissao} disabled={dis}
            onChange={e => set('nao_gera_comissao', e.target.checked)} />
          <label htmlFor="chkNaoGeraComissao">Não gera comissão</label>
        </div>

        <div className={s.checkRow} title="Marcado, a NFS-e emitida para este cliente sai com o ISS retido pelo tomador, na alíquota de Minha Empresa → NFS-e">
          <input type="checkbox" id="chkIssRetido"
            checked={form.nfse_iss_retido} disabled={dis}
            onChange={e => set('nfse_iss_retido', e.target.checked)} />
          <label htmlFor="chkIssRetido">Retém ISS na NFS-e (tomador responsável pela retenção)</label>
        </div>
      </div>

      <div className={s.fieldset}>
        <div className={s.legend}>Fidelidade</div>

        <div className={s.campo}>
          <span className={s.campoLabel}>Plano</span>
          <input className={s.inputErp} style={{ flex: 1 }}
            value={form.plano_fidelidade} readOnly={dis}
            onChange={e => set('plano_fidelidade', e.target.value)} />
        </div>

        <div className={s.campo}>
          <span className={s.campoLabel}>Cartão</span>
          <input className={s.inputErp} style={{ flex: 1 }}
            value={form.cartao_fidelidade} readOnly={dis}
            onChange={e => set('cartao_fidelidade', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Novo/Editar participante
   ──────────────────────────────────────────────────────── */
function ModalParticipante({ item, onSalvar, onClose, salvando, erro, cfg }) {
  const [nome, setNome]     = useState(item.nome || '');
  const [tipo, setTipo]     = useState(item.tipo || '');
  const [obs, setObs]       = useState(item.detalhes?.obs || '');
  const listId = 'tipos-participante-' + cfg.labelAba.replace(/\W+/g, '');

  return (
    <div className={s.overlay} data-enter-scope="off">
      <div className={s.modal} style={{ width: 420 }}>
        <div className={s.modalTitle}>{item.id ? `Editar ${cfg.labelSingular}` : `Novo(a) ${cfg.labelSingular}`}</div>
        <div className={s.modalBody} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {erro && <div style={{ color: '#c0392b', fontSize: 12 }}>{erro}</div>}
          <div className={s.campo}>
            <span className={s.campoLabel}>Nome*</span>
            <input className={s.inputErp} style={{ flex: 1 }} autoFocus
              value={nome} onChange={e => setNome(e.target.value)}
              placeholder={cfg.nomePlaceholder} />
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>Tipo</span>
            <input className={s.inputErp} style={{ flex: 1 }} list={listId}
              value={tipo} onChange={e => setTipo(e.target.value)}
              placeholder={cfg.tipoPlaceholder} />
            <datalist id={listId}>
              {cfg.tipoSugestoes.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div className={s.campo} style={{ alignItems: 'flex-start' }}>
            <span className={s.campoLabel} style={{ paddingTop: 6 }}>Detalhes</span>
            <textarea className={s.textareaObs} style={{ flex: 1, minHeight: 70 }}
              value={obs} onChange={e => setObs(e.target.value)}
              placeholder={cfg.detalhesPlaceholder} />
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={!nome.trim() || salvando}
            onClick={() => onSalvar({ ...item, nome: nome.trim(), tipo: tipo.trim() || null, detalhes: obs.trim() ? { obs: obs.trim() } : {} })}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Movimento/Pedidos — Aberto = pedido/orçamento não finalizado;
   Faturado = venda concluída (baixou estoque e gerou financeiro) e
   devolução de venda (valor negativo). Só consulta.
   ──────────────────────────────────────────────────────── */
const TIPO_MOV = { venda: 'Venda', pedido: 'Pedido', orcamento: 'Orçamento', devolucao: 'Devolução' };
const STATUS_ABERTO = { aberto: 'Aberto', agendado: 'Agendado', aprovado: 'Aprovado', em_atendimento: 'Em atendimento' };
const fmtMoedaMov = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDataMov = v => v ? v.split('-').reverse().join('/') : '—';

function descricaoMovimento(m) {
  if (m.tipo === 'devolucao') return `Devolução da venda nº ${m.origem_numero}${m.status === 'pendente_caixa' ? ' (pendente no caixa)' : ''}`;
  if (m.tipo === 'venda') return m.origem_tipo ? `Venda do ${TIPO_MOV[m.origem_tipo]?.toLowerCase() || m.origem_tipo} nº ${m.origem_numero}` : 'Venda (PDV)';
  return `${TIPO_MOV[m.tipo] || m.tipo} — ${STATUS_ABERTO[m.status] || m.status}`;
}

function AbaMovimento({ clienteId }) {
  const [filtro, setFiltro] = useState(() => ({ ini: somarMeses(hojeBR(), -3), fim: hojeBR(), situacao: '' }));
  const [aplicado, setAplicado] = useState(filtro);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['cliente-movimento', clienteId, aplicado],
    queryFn: () => api.get(`/clientes/${clienteId}/movimento`, {
      params: {
        data_ini: aplicado.ini || undefined,
        data_fim: aplicado.fim || undefined,
        situacao: aplicado.situacao || undefined,
      },
    }).then(r => r.data),
    enabled: !!clienteId,
  });

  if (!clienteId) {
    return <div className={s.abaPlaceholder}>Salve o cadastro do cliente para ver o movimento.</div>;
  }

  const itens = data?.itens || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
        <label>De:</label>
        <InputDataFiltro className={s.inputErp} style={{ width: 130 }} value={filtro.ini}
          onChange={e => setFiltro(f => ({ ...f, ini: e.target.value }))} />
        <label>Até:</label>
        <InputDataFiltro className={s.inputErp} style={{ width: 130 }} value={filtro.fim}
          onChange={e => setFiltro(f => ({ ...f, fim: e.target.value }))} />
        <label>Status:</label>
        <select className={s.selectErp} style={{ width: 130 }} value={filtro.situacao}
          onChange={e => setFiltro(f => ({ ...f, situacao: e.target.value }))}>
          <option value="">Todos</option>
          <option value="aberto">Abertos</option>
          <option value="faturado">Faturados</option>
        </select>
        <button className={s.btnErp} onClick={() => setAplicado({ ...filtro })} disabled={isFetching}>
          {isFetching ? 'Carregando...' : 'Filtrar'}
        </button>
      </div>

      {isError ? (
        <div className={s.abaPlaceholder}>Não foi possível carregar o movimento.</div>
      ) : !itens.length ? (
        <div className={s.abaPlaceholder}>{isFetching ? 'Carregando...' : 'Nenhum movimento no período.'}</div>
      ) : (
        <div style={{ overflow: 'auto', maxHeight: 360 }}>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Data</th>
                <th style={{ width: 90 }}>Tipo</th>
                <th style={{ width: 70 }}>Nº</th>
                <th>Descrição</th>
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 110, textAlign: 'right' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {itens.map(m => (
                <tr key={`${m.tipo}-${m.id}`}>
                  <td style={{ cursor: 'default' }}>{fmtDataMov(m.data)}</td>
                  <td style={{ cursor: 'default' }}>{TIPO_MOV[m.tipo] || m.tipo}</td>
                  <td style={{ cursor: 'default' }}>{m.numero ?? '—'}</td>
                  <td style={{ cursor: 'default' }}>{descricaoMovimento(m)}</td>
                  <td style={{ cursor: 'default', fontWeight: 600, color: m.situacao === 'aberto' ? '#e65100' : '#1b7a3d' }}>
                    {m.situacao === 'aberto' ? 'Aberto' : 'Faturado'}
                  </td>
                  <td style={{ cursor: 'default', textAlign: 'right', color: m.valor < 0 ? '#c62828' : undefined }}>
                    {fmtMoedaMov(m.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end', fontSize: 13, fontWeight: 600 }}>
          {aplicado.situacao !== 'faturado' && <span>Total aberto: {fmtMoedaMov(data.total_aberto)}</span>}
          {aplicado.situacao !== 'aberto' && <span>Total faturado: {fmtMoedaMov(data.total_faturado)}</span>}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Aba Participantes — animal de estimação, veículo, dependente etc. que o
   cliente traz pro atendimento (usado pela Agenda quando o segmento da
   organização habilita agendamento). CRUD próprio, salva na hora (não faz
   parte do form do cliente) — por isso funciona independente do modo
   Alterar/Novo da tela.
   ──────────────────────────────────────────────────────── */
function AbaParticipantes({ clienteId, cfg }) {
  const qc = useQueryClient();
  const [editItem, setEditItem] = useState(null); // {} = novo, {...} = editando, null = fechado
  const [erro, setErro] = useState('');

  const { data: lista = [], isLoading } = useQuery({
    queryKey: ['participantes', clienteId],
    queryFn: () => api.get(`/clientes/${clienteId}/participantes`).then(r => r.data),
    enabled: !!clienteId,
  });

  const salvar = useMutation({
    mutationFn: (p) => p.id
      ? api.put(`/clientes/${clienteId}/participantes/${p.id}`, p)
      : api.post(`/clientes/${clienteId}/participantes`, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['participantes', clienteId] });
      setEditItem(null);
      setErro('');
    },
    onError: (e) => setErro(e.response?.data?.erro || 'Não foi possível salvar o participante.'),
  });

  const remover = useMutation({
    mutationFn: (id) => api.delete(`/clientes/${clienteId}/participantes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['participantes', clienteId] }),
  });

  if (!clienteId) {
    return (
      <div className={s.abaPlaceholder}>
        Salve o cadastro do cliente para poder adicionar {cfg.labelAba.toLowerCase()} ({cfg.tipoPlaceholder}).
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {cfg.tipoPlaceholder} etc. — o que o cliente traz pro atendimento.
        </span>
        <button className={s.btnErp} onClick={() => { setErro(''); setEditItem({ nome: '', tipo: '', detalhes: {} }); }}>
          + Novo
        </button>
      </div>

      {isLoading ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Carregando...</div>
      ) : !lista.length ? (
        <div className={s.abaPlaceholder}>Nenhum participante cadastrado.</div>
      ) : (
        <table className={s.modalTabela}>
          <thead>
            <tr>
              <th>Nome</th>
              <th style={{ width: 160 }}>Tipo</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {lista.map(p => (
              <tr key={p.id}>
                <td style={{ cursor: 'default' }}>
                  {p.nome}
                  {p.detalhes?.obs && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.detalhes.obs}</div>
                  )}
                </td>
                <td style={{ cursor: 'default' }}>{p.tipo || '—'}</td>
                <td style={{ cursor: 'default', whiteSpace: 'nowrap' }}>
                  <button className={s.btnLookup} title="Editar"
                    onClick={() => { setErro(''); setEditItem(p); }}>✎</button>
                  <button className={s.btnLookup} title="Excluir" style={{ marginLeft: 4 }}
                    onClick={() => window.confirm(`Excluir "${p.nome}"?`) && remover.mutate(p.id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editItem && (
        <ModalParticipante
          item={editItem}
          salvando={salvar.isPending}
          erro={erro}
          cfg={cfg}
          onSalvar={(p) => salvar.mutate(p)}
          onClose={() => { setEditItem(null); setErro(''); }}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Componente principal
   ──────────────────────────────────────────────────────── */
export default function CadastroClientes() {
  const qc = useQueryClient();
  const { usuario } = useAuth();
  const cfgParticipante = configParticipante(usuario?.segmento_nome);
  // Aba Recorrência só com Parâmetros → Vendas → "Habilitar recorrência".
  const recorrenciaHabilitada = useRecorrenciaHabilitada();
  const ABAS = [
    ...ABAS_BASE.map(a => a.id === 'participantes' ? { ...a, label: cfgParticipante.labelAba } : a),
    ...(recorrenciaHabilitada ? [{ id: 'recorrencia', label: 'Recorrência' }] : []),
  ];
  const [aba, setAba]           = useState('geral');
  const [modo, setModo]         = useState('nav');   // 'nav' | 'novo' | 'editar'
  const [posicao, setPosicao]   = useState(0);
  const [form, setForm]         = useState(FORM_VAZIO);
  const [modalBusca, setModalBusca]   = useState(false);
  const [modalTabela, setModalTabela] = useState(false);
  const [erroSalvar, setErroSalvar]   = useState('');

  const editando = modo !== 'nav';

  /* lista para navegação */
  const { data: lista = [] } = useQuery({
    queryKey: ['clientes-lista'],
    queryFn: () => api.get('/clientes', { params: { todos: 'true' } }).then(r => r.data),
  });

  /* carrega cliente completo */
  const carregarCliente = useCallback(async (id) => {
    if (!id) return;
    const { data } = await api.get(`/clientes/${id}`);
    setForm(clienteParaForm(data));
    setModo('nav');
  }, []);

  /* inicializa no primeiro da lista */
  useEffect(() => {
    if (lista.length && !form.id) {
      carregarCliente(lista[0].id);
    }
  }, [lista]); // eslint-disable-line

  /* ── Ações ── */
  const handleNovo = async () => {
    setErroSalvar('');
    try {
      const { data } = await api.get('/clientes/proximo-codigo');
      setForm({ ...FORM_VAZIO, codigo: data.proximo });
    } catch {
      setForm({ ...FORM_VAZIO, codigo: 'Automático' });
    }
    setModo('novo');
  };

  const handleAlterar = () => {
    if (!form.id) return alert('Selecione um cliente antes de alterar.');
    setErroSalvar('');
    setModo('editar');
  };

  const handleCancelar = () => {
    setErroSalvar('');
    if (form.id) carregarCliente(form.id);
    else setForm(FORM_VAZIO);
    setModo('nav');
  };

  const criarMutation = useMutation({
    mutationFn: d => api.post('/clientes', d),
    onSuccess: async ({ data }) => {
      setErroSalvar('');
      qc.invalidateQueries(['clientes-lista']);
      await carregarCliente(data.id);
      qc.invalidateQueries(['clientes-lista']).then(() => {
        const idx = lista.findIndex(c => c.id === data.id);
        if (idx >= 0) setPosicao(idx);
      });
    },
    onError: err => {
      const msg = err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || err.message;
      setErroSalvar('Erro ao salvar: ' + msg);
    },
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id, d }) => api.put(`/clientes/${id}`, d),
    onSuccess: async (_, { id }) => {
      setErroSalvar('');
      qc.invalidateQueries(['clientes-lista']);
      await carregarCliente(id);
    },
    onError: err => {
      const msg = err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || err.message;
      setErroSalvar('Erro ao salvar: ' + msg);
    },
  });

  const handleSalvar = async () => {
    setErroSalvar('');
    if (!form.nome?.trim()) { setErroSalvar('Informe o nome do cliente.'); return; }

    // CNPJ alfanumérico (NT 2026.004): valida DV antes de salvar
    const erroDoc = erroDocumento(form.cpf_cnpj);
    if (erroDoc) { setErroSalvar(erroDoc); return; }

    // Valida duplicata de CPF/CNPJ apenas entre registros ativos com nome válido
    const docLimpo = limparDocumento(form.cpf_cnpj);
    if (docLimpo) {
      try {
        const { data: encontrados } = await api.get('/clientes', {
          params: { busca: docLimpo, todos: 'true' },
        });
        const duplicata = encontrados.find(c =>
          limparDocumento(c.cpf_cnpj) === docLimpo &&
          c.id !== form.id &&
          c.nome?.trim()   // ignora registros sem nome
        );
        if (duplicata) {
          setErroSalvar(
            `CPF/CNPJ já cadastrado para o cliente "${duplicata.nome}" (Cód. ${duplicata.codigo}).`
          );
          return;
        }
      } catch { /* se a API falhar na validação, deixa prosseguir */ }
    }

    const payload = {
      nome:             form.nome.trim(),
      nome_fantasia:    form.nome_fantasia?.trim() || null,
      tipo_pessoa:      form.tipo_pessoa,
      inativo:          form.inativo,
      cpf_cnpj:         form.cpf_cnpj  || null,
      rg:               form.rg        || null,
      ie:               form.ie        || null,
      tipo_contribuinte: form.tipo_contribuinte,
      profissao:        form.profissao || null,
      data_nascimento:  form.data_nascimento || null,
      cep:              form.cep        || null,
      logradouro:       form.logradouro || null,
      numero:           form.numero     || null,
      complemento:      form.complemento || null,
      bairro:           form.bairro    || null,
      cidade:           form.cidade    || null,
      estado:           form.estado    || null,
      ponto_referencia: form.ponto_referencia || null,
      email:            form.email     || null,
      site:             form.site      || null,
      fone1:            form.fone1     || null,
      fone2:            form.fone2     || null,
      fone3:            form.fone3     || null,
      limite_credito:   form.limite_credito === '' ? null : parseFloat(form.limite_credito),
      desconto_perc:    parseFloat(form.desconto_perc)   || 0,
      tabela_preco_id:  form.tabela_preco_id || null,
      plano_fidelidade: form.plano_fidelidade  || null,
      cartao_fidelidade: form.cartao_fidelidade || null,
      nao_gera_comissao: form.nao_gera_comissao,
      nfse_iss_retido: form.nfse_iss_retido,
      obs:              form.obs || null,
    };
    if (modo === 'novo') criarMutation.mutate(payload);
    else atualizarMutation.mutate({ id: form.id, d: payload });
  };

  // Exclusão exige senha de admin (backend valida movimentações e devolve
  // 409 antes de pedir a senha; 401 exige_admin → pedirSenhaAdmin → repete)
  const excluirComSenha = async (id) => {
    try {
      await api.delete(`/clientes/${id}`);
      return true;
    } catch (err) {
      if (!err.response?.data?.exige_admin) throw err;
      const senha = await pedirSenhaAdmin(
        `Excluir o cliente "${form.nome}" exige autorização de administrador.`,
        { acao: 'excluir_cliente', detalhes: `Cliente #${id} — ${form.nome}` });
      if (senha === null) return false;
      await api.delete(`/clientes/${id}`, { data: { senha_admin: senha } });
      return true;
    }
  };

  const handleExcluir = async () => {
    if (!form.id) return;
    if (!window.confirm(`Excluir o cliente "${form.nome}"?`)) return;
    try {
      if (!(await excluirComSenha(form.id))) return;
      qc.invalidateQueries(['clientes-lista']);
      setForm(FORM_VAZIO);
      setModo('nav');
      alert('Cliente excluído.');
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir cliente.');
    }
  };

  /* Navegação <| < > >| */
  const navPrimeiro = () => {
    if (lista.length && posicao !== 0) {
      setPosicao(0);
      carregarCliente(lista[0].id);
    }
  };
  const navAnterior = () => {
    if (posicao > 0) {
      const nova = posicao - 1;
      setPosicao(nova);
      carregarCliente(lista[nova].id);
    }
  };
  const navProximo = () => {
    if (posicao < lista.length - 1) {
      const nova = posicao + 1;
      setPosicao(nova);
      carregarCliente(lista[nova].id);
    }
  };
  const navUltimo = () => {
    if (lista.length && posicao !== lista.length - 1) {
      const ultima = lista.length - 1;
      setPosicao(ultima);
      carregarCliente(lista[ultima].id);
    }
  };

  const isSaving = criarMutation.isPending || atualizarMutation.isPending;

  /* Atalhos de teclado — mesmo esquema das demais telas de cadastro
     (Clientes/Produtos/Usuários): F2 Alterar, F3 Buscar, F4 Novo,
     F8 Excluir, F9 Salvar, Esc Cancelar. */
  useEffect(() => {
    const handler = (e) => {
      if (modalBusca || modalTabela) return; // modais próprios cuidam do próprio teclado
      if (e.key === 'F4' && !editando) { e.preventDefault(); handleNovo(); return; }
      if (e.key === 'F3' && !editando) { e.preventDefault(); setModalBusca(true); return; }
      if (e.key === 'F2' && !editando && form.id) { e.preventDefault(); handleAlterar(); return; }
      if (e.key === 'F8' && !editando && form.id) { e.preventDefault(); handleExcluir(); return; }
      if (e.key === 'F9' && editando && !isSaving) { e.preventDefault(); handleSalvar(); return; }
      if (e.key === 'Escape' && editando) { e.preventDefault(); handleCancelar(); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editando, form.id, modalBusca, modalTabela, isSaving]); // eslint-disable-line

  /* Status bar */
  const statusText = () => {
    if (erroSalvar) return <span style={{ color: '#c00', fontWeight: 'bold' }}>⚠ {erroSalvar}</span>;
    if (modo === 'novo')   return <span className={s.statusNovo}>INSERINDO NOVO CLIENTE</span>;
    if (modo === 'editar') return <span className={s.statusEditar}>ALTERANDO CLIENTE</span>;
    if (form.id) return `Cliente ${form.codigo} — ${lista.length} registro(s) | Pos: ${posicao + 1}`;
    return 'Nenhum cliente carregado';
  };

  /* ── Render ── */
  return (
    <>
      <div className={s.erpWrap} data-enter-scope="on" onKeyDown={navegarComEnter}>

        {/* Barra de título */}
        <div className={s.titleBar}>
          <span>👥</span> Cadastro de Clientes
        </div>

        {/* Cabeçalho */}
        <div className={s.headerRow}>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Código</span>
            <input className={s.inputErp} style={{ width: 70 }}
              value={form.codigo} readOnly />
          </div>

          <div className={s.headerGroup} style={{ flex: 1 }}>
            <span className={s.headerLabel}>Nome</span>
            <input className={s.inputErp} style={{ flex: 1 }}
              value={form.nome} readOnly={!editando}
              onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
              placeholder={editando ? 'Nome completo ou razão social...' : ''} />
          </div>

          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Tipo</span>
            <label className={s.radioLabel} style={{ marginLeft: 4 }}>
              <input type="radio" name="tipo_pessoa" value="F"
                checked={form.tipo_pessoa === 'F'} disabled={!editando}
                onChange={() => setForm(f => ({ ...f, tipo_pessoa: 'F' }))} />
              Pessoa Física
            </label>
            <label className={s.radioLabel} style={{ marginLeft: 8 }}>
              <input type="radio" name="tipo_pessoa" value="J"
                checked={form.tipo_pessoa === 'J'} disabled={!editando}
                onChange={() => setForm(f => ({ ...f, tipo_pessoa: 'J' }))} />
              Pessoa Jurídica
            </label>
          </div>

          <div className={s.checkRow} style={{ margin: 0 }}>
            <input type="checkbox" id="chkInativo"
              checked={form.inativo} disabled={!editando}
              onChange={e => setForm(f => ({ ...f, inativo: e.target.checked }))} />
            <label htmlFor="chkInativo">Inativo</label>
          </div>
        </div>

        {/* Abas */}
        <div className={s.tabBar}>
          {ABAS.map(a => (
            <button key={a.id}
              className={`${s.tab} ${aba === a.id ? s.tabAtiva : ''}`}
              onClick={() => setAba(a.id)}>
              {a.label}
            </button>
          ))}
        </div>

        {/* Conteúdo */}
        <div className={s.tabContent}>
          {aba === 'geral' ? (
            <AbaGeral form={form} setForm={setForm} editando={editando} />
          ) : aba === 'financeiro' ? (
            <AbaFinanceiro
              form={form} setForm={setForm} editando={editando}
              onAbrirTabela={() => setModalTabela(true)}
            />
          ) : aba === 'movimento' ? (
            <AbaMovimento clienteId={form.id} />
          ) : aba === 'participantes' ? (
            <AbaParticipantes clienteId={form.id} cfg={cfgParticipante} />
          ) : aba === 'recorrencia' && recorrenciaHabilitada ? (
            <AbaRecorrencia cliente={form} />
          ) : aba === 'obs' ? (
            <textarea className={s.textareaObs}
              readOnly={!editando}
              value={form.obs}
              onChange={e => setForm(f => ({ ...f, obs: e.target.value }))}
              placeholder={editando ? 'Observações sobre o cliente...' : ''} />
          ) : (
            <div className={s.abaPlaceholder}>
              Aba "{ABAS.find(a => a.id === aba)?.label}" — em desenvolvimento
            </div>
          )}
        </div>

        {/* Barra de status */}
        <div className={s.statusBar}>{statusText()}</div>

        {/* Rodapé */}
        <div className={s.footer}>
          <div className={s.footerLeft}>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navPrimeiro} disabled={editando || posicao === 0} title="Primeiro">
              &lt;|
            </button>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navAnterior} disabled={editando || posicao === 0} title="Anterior">
              &lt;
            </button>
            <span className={s.footerPos}>
              {lista.length ? `${posicao + 1} / ${lista.length}` : '0 / 0'}
            </span>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navProximo} disabled={editando || posicao >= lista.length - 1} title="Próximo">
              &gt;
            </button>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navUltimo} disabled={editando || posicao >= lista.length - 1} title="Último">
              &gt;|
            </button>
          </div>

          <div className={s.footerRight}>
            <button className={s.btnErp} onClick={handleNovo} disabled={editando}>
              Novo (F4)
            </button>
            <button className={s.btnErp} onClick={() => setModalBusca(true)} disabled={editando}>
              Buscar (F3)
            </button>
            <button className={s.btnErp} onClick={handleAlterar} disabled={editando || !form.id}>
              Alterar (F2)
            </button>
            <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>
              Cancelar (Esc)
            </button>
            <button className={`${s.btnErp} ${s.btnErpPerigo}`}
              onClick={handleExcluir} disabled={editando || !form.id}>
              Excluir (F8)
            </button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} data-enter-final
              onClick={handleSalvar} disabled={!editando || isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar (F9)'}
            </button>
            <div className={s.footerSep} />
            <button className={s.btnErp} onClick={() => window.history.back()}>
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* ── Modais ── */}
      {modalBusca && (
        <ModalBuscaCliente
          onSelect={async c => {
            const idx = lista.findIndex(l => l.id === c.id);
            if (idx >= 0) setPosicao(idx);
            await carregarCliente(c.id);
            setModalBusca(false);
          }}
          onClose={() => setModalBusca(false)}
        />
      )}

      {modalTabela && (
        <ModalBuscaTabela
          onSelect={t => {
            setForm(f => ({ ...f, tabela_preco_id: t.id, tabela_preco_nome: t.descricao }));
            setModalTabela(false);
          }}
          onClose={() => setModalTabela(false)}
        />
      )}
    </>
  );
}
